import {
  SensorType,
  useGetLeakDetectionData as useGetLeakDetectionDataCommon,
  LeakThreshold,
  LeakColor,
  extractReadingValueFromSensorEvent,
  LeakDetectorReading,
  useSensorAlerts,
  SensorAlertType,
} from 'common';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { gradients } from 'tamagui-config';

import { useLatestSensorEvents } from '../sensors/useLatestSensorEvent';

export const useGetLeakDetectionData = () => {
  const { t } = useTranslation();
  const { event: leakDetectorEvent, sensor: leakDetectorSensor } = useLatestSensorEvents(
    SensorType.LEAK_DETECTOR,
  );

  const { sensorAlerts, loading: _alertsLoading } = useSensorAlerts(leakDetectorEvent?.sensorId);

  const humidity =
    (leakDetectorEvent
      ? extractReadingValueFromSensorEvent<LeakDetectorReading>(leakDetectorEvent)?.humidity
      : null) ?? 0;
  const leak = leakDetectorEvent
    ? extractReadingValueFromSensorEvent<LeakDetectorReading>(leakDetectorEvent)?.leak
    : null;

  const hasActiveLeakAlert = useMemo(() => {
    if (!sensorAlerts || sensorAlerts.length === 0) {
      return false;
    }

    const now = Date.now();
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const filteredAlerts = sensorAlerts.filter((alert) => {
      const isValidStatus = alert.status === 'new' || alert.status === 'ongoing';
      // Only include LEAK alerts as leak-related alerts, not humidity alerts
      const isLeakRelatedAlert = alert.alertType.includes(SensorAlertType.Leak);
      const isNotAcknowledged = alert.acknowledgedAt === null && alert.acknowledgedBy === null;
      const isWithinTimeWindow = alert.timestamp && alert.timestamp * 1000 >= twentyFourHoursAgo;

      return isValidStatus && isLeakRelatedAlert && isNotAcknowledged && isWithinTimeWindow;
    });

    const hasActiveAlert = filteredAlerts.length > 0;

    return hasActiveAlert;
  }, [sensorAlerts]);

  const activeLeakAlert = useMemo(() => {
    if (!sensorAlerts || sensorAlerts.length === 0) return null;

    const now = Date.now();
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    const activeLeakAlerts = sensorAlerts.filter(
      (alert) =>
        (alert.status === 'new' || alert.status === 'ongoing') &&
        alert.alertType.includes(SensorAlertType.Leak) &&
        alert.acknowledgedAt === null &&
        alert.acknowledgedBy === null &&
        alert.timestamp &&
        alert.timestamp * 1000 >= twentyFourHoursAgo,
    );
    if (activeLeakAlerts.length === 0) return null;
    return activeLeakAlerts.reduce(
      (mostRecent, alert) =>
        !mostRecent ||
        (alert.timestamp && mostRecent.timestamp && alert.timestamp > mostRecent.timestamp)
          ? alert
          : mostRecent,
      activeLeakAlerts[0],
    );
  }, [sensorAlerts]);

  const leakDetected = hasActiveLeakAlert || (leak ? Boolean(leak) : false);

  const timestamp = activeLeakAlert?.timestamp || leakDetectorEvent?.timestamp;

  const { status, ...rest } = useGetLeakDetectionDataCommon({
    humidity,
    leakDetected,
    timestamp,
  });

  const statusText = useMemo(() => {
    switch (status) {
      case 'leakDetected':
        return t('water.leakDetected');
      case 'extremelyHumid':
        return t('water.extremelyHumid');
      case 'highHumidity':
        return t('water.highHumidity');
      case 'slightlyHumid':
        return t('water.slightlyHumid');
      case 'noLeak':
        return t('water.noLeak');
      default:
        return t('water.invalidData');
    }
  }, [status, t]);

  const gradientColors = useMemo(() => {
    const color =
      status === LeakThreshold.LeakDetected ||
      status === LeakThreshold.ExtremelyHumid ||
      status === LeakThreshold.HighHumidity
        ? LeakColor.Red
        : status === LeakThreshold.SlightlyHumid
          ? LeakColor.Yellow
          : LeakColor.Green;

    return mapGradientColors(color);
  }, [status]);

  return {
    ...rest,
    status,
    statusText,
    title: leakDetectorSensor?.location || '',
    subtitle: statusText,
    lastReported: rest.timestamp,
    leakDetected: status === LeakThreshold.LeakDetected,
    gradientColors,
  };
};

const mapGradientColors = (color: LeakColor | undefined) => {
  if (color === LeakColor.Green) {
    return gradients.success as [string, string, string];
  }
  if (color === LeakColor.Yellow) {
    return gradients.warning as [string, string, string];
  }
  return gradients.error as [string, string, string];
};
